import { Link, useSearch } from "@tanstack/react-router";
import { useDeferredValue, useState } from "react";
import { useRole } from "../../components/AppShell";
import { PlusIcon, SearchIcon } from "../../components/icons";
import { TabNav } from "../../components/TabNav";
import { Avatar, Empty, ListSkeleton } from "../../components/ui";
import { formatPhone, shortTime } from "../../lib/format";
import {
  type CaseFilter,
  useCaseStatuses,
  useCases,
  usePartRequestStatuses,
  usePartRequests,
  useTeam,
} from "../../lib/queries";
import { CaseDialog } from "./CaseDialog";
import { caseNumber, KIND_LABEL, partRequestNumber } from "./case-text";
import { PartRequestDialog } from "./PartRequestDialog";
import { StatusesDialog } from "./StatusesDialog";

const VIEWS: { key: string; label: string; filter: CaseFilter }[] = [
  { key: "open", label: "Abiertos", filter: { state: "open" } },
  { key: "mine", label: "Míos", filter: { state: "open", assignee: "me" } },
  { key: "unassigned", label: "Sin responsable", filter: { state: "open", assignee: "unassigned" } },
  { key: "closed", label: "Cerrados", filter: { state: "closed" } },
  { key: "all", label: "Todos", filter: { state: "all" } },
];

/** After-sales: cases (faults, exchanges, returns) and the spare parts customers ask for. */
export function CasesPage() {
  const { tab } = useSearch({ from: "/app/postventa" });
  return (
    <div className="page">
      <div className="page-head">
        <h1>Postventa</h1>
      </div>
      <TabNav label="Postventa">
        <Link
          to="/postventa"
          search={{}}
          activeOptions={{ exact: true, includeSearch: true }}
          aria-current={tab ? undefined : "page"}
        >
          Casos
        </Link>
        <Link
          to="/postventa"
          search={{ tab: "repuestos" }}
          activeOptions={{ exact: true, includeSearch: true }}
          aria-current={tab === "repuestos" ? "page" : undefined}
        >
          Repuestos
        </Link>
      </TabNav>
      {tab === "repuestos" ? <PartRequestsList /> : <CasesList />}
    </div>
  );
}

/** The views, the status filter and the search, shared by both lists. */
function useListFilters() {
  const [view, setView] = useState("open");
  const [statusId, setStatusId] = useState("");
  const [q, setQ] = useState("");
  const deferred = useDeferredValue(q.trim());
  const filter = { ...VIEWS.find((v) => v.key === view)?.filter, statusId, q: deferred };
  return { view, setView, statusId, setStatusId, q, setQ, filter, searching: !!(deferred || statusId) };
}

function Filters({
  f,
  statuses,
  search,
  actions,
}: {
  f: ReturnType<typeof useListFilters>;
  statuses: { id: string; label: string }[];
  search: { placeholder: string; label: string };
  actions: React.ReactNode;
}) {
  return (
    <div className="page-head">
      <label className="search-field">
        <SearchIcon />
        <input
          className="input"
          type="search"
          placeholder={search.placeholder}
          aria-label={search.label}
          value={f.q}
          onChange={(e) => f.setQ(e.target.value)}
        />
      </label>
      <fieldset className="filters">
        <legend className="sr-only">Mostrar</legend>
        {VIEWS.map((v) => (
          <button key={v.key} type="button" aria-pressed={f.view === v.key} onClick={() => f.setView(v.key)}>
            {v.label}
          </button>
        ))}
      </fieldset>
      <select
        className="select products-category"
        aria-label="Estado"
        value={f.statusId}
        onChange={(e) => f.setStatusId(e.target.value)}
      >
        <option value="">Todos los estados</option>
        {statuses.map((s) => (
          <option key={s.id} value={s.id}>
            {s.label}
          </option>
        ))}
      </select>
      <div className="products-actions">{actions}</div>
    </div>
  );
}

function CasesList() {
  const role = useRole();
  const f = useListFilters();
  const list = useCases(f.filter);
  const statuses = useCaseStatuses().data ?? [];
  const team = useTeam().data?.items ?? [];
  const names = new Map(team.map((m) => [m.id, m.name]));
  const [creating, setCreating] = useState(false);
  const [editingStatuses, setEditingStatuses] = useState(false);
  const items = list.data ?? [];

  return (
    <>
      <Filters
        f={f}
        statuses={statuses}
        search={{ placeholder: "Buscar C-0042, referencia, cliente o modelo", label: "Buscar casos" }}
        actions={
          <>
            {(role === "owner" || role === "admin") && (
              <button className="btn" type="button" onClick={() => setEditingStatuses(true)}>
                Estados
              </button>
            )}
            <button className="btn btn-primary" type="button" onClick={() => setCreating(true)}>
              <PlusIcon />
              Nuevo caso
            </button>
          </>
        }
      />
      <div className="card">
        {list.isPending ? (
          <ListSkeleton />
        ) : items.length === 0 ? (
          <Empty title={f.searching ? "Ningún caso coincide." : "No hay casos aquí."}>
            <span>
              {f.searching
                ? "Prueba con otro número, nombre o estado."
                : "Los casos se abren desde la conversación del cliente, con el botón Crear caso."}
            </span>
          </Empty>
        ) : (
          <ul className="entity-list">
            {items.map((c) => (
              <li key={c.id}>
                <Link to="/postventa/$caseId" params={{ caseId: c.id }} className="entity-row case-row">
                  <span className="case-number mono">{caseNumber(c.number)}</span>
                  <span className="entity-main">
                    <span className="entity-name">{c.title}</span>
                    <span className="entity-meta">
                      <span>{c.contactName ?? formatPhone(c.contactWaId)}</span>
                      {(c.productName ?? c.model) && <span>{c.productName ?? c.model}</span>}
                      <span>{KIND_LABEL[c.kind]}</span>
                      {c.reference && <span className="mono">{c.reference}</span>}
                    </span>
                  </span>
                  <span className="entity-flags">
                    <span className={`pill ${c.closed ? "pill-closed" : "pill-human"}`}>{c.statusLabel}</span>
                    {c.assigneeId && (
                      <span className="case-assignee" title={names.get(c.assigneeId)}>
                        <Avatar name={names.get(c.assigneeId) ?? null} fallback="?" />
                      </span>
                    )}
                    <span className="faint entity-time">{shortTime(c.updatedAt)}</span>
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </div>
      {creating && <CaseDialog onClose={() => setCreating(false)} />}
      {editingStatuses && <StatusesDialog onClose={() => setEditingStatuses(false)} />}
    </>
  );
}

function PartRequestsList() {
  const role = useRole();
  const f = useListFilters();
  const list = usePartRequests(f.filter);
  const statuses = usePartRequestStatuses().data ?? [];
  const team = useTeam().data?.items ?? [];
  const names = new Map(team.map((m) => [m.id, m.name]));
  const [creating, setCreating] = useState(false);
  const [editingStatuses, setEditingStatuses] = useState(false);
  const items = list.data ?? [];

  return (
    <>
      <Filters
        f={f}
        statuses={statuses}
        search={{ placeholder: "Buscar R-0012, pieza, cliente o modelo", label: "Buscar solicitudes de repuesto" }}
        actions={
          <>
            {(role === "owner" || role === "admin") && (
              <button className="btn" type="button" onClick={() => setEditingStatuses(true)}>
                Estados
              </button>
            )}
            <button className="btn btn-primary" type="button" onClick={() => setCreating(true)}>
              <PlusIcon />
              Pedir repuesto
            </button>
          </>
        }
      />
      <div className="card">
        {list.isPending ? (
          <ListSkeleton />
        ) : items.length === 0 ? (
          <Empty title={f.searching ? "Ninguna solicitud coincide." : "No hay solicitudes de repuesto aquí."}>
            <span>
              {f.searching
                ? "Prueba con otro número, pieza o estado."
                : "Se registran desde la conversación del cliente o desde un caso, con el botón Pedir repuesto."}
            </span>
          </Empty>
        ) : (
          <ul className="entity-list">
            {items.map((r) => (
              <li key={r.id}>
                <Link to="/postventa/repuestos/$requestId" params={{ requestId: r.id }} className="entity-row case-row">
                  <span className="case-number mono">{partRequestNumber(r.number)}</span>
                  <span className="entity-main">
                    <span className="entity-name">
                      {r.quantity > 1 ? `${r.quantity} × ` : ""}
                      {r.partName}
                    </span>
                    <span className="entity-meta">
                      <span>{r.contactName ?? formatPhone(r.contactWaId)}</span>
                      {(r.productName ?? r.model) && <span>Para {r.productName ?? r.model}</span>}
                      {r.partModel && <span className="mono">{r.partModel}</span>}
                      {r.reference && <span className="mono">{r.reference}</span>}
                    </span>
                  </span>
                  <span className="entity-flags">
                    <span className={`pill ${r.closed ? "pill-closed" : r.arrived ? "pill-bot" : "pill-human"}`}>
                      {r.statusLabel}
                    </span>
                    {r.assigneeId && (
                      <span className="case-assignee" title={names.get(r.assigneeId)}>
                        <Avatar name={names.get(r.assigneeId) ?? null} fallback="?" />
                      </span>
                    )}
                    <span className="faint entity-time">{shortTime(r.updatedAt)}</span>
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </div>
      {creating && <PartRequestDialog onClose={() => setCreating(false)} />}
      {editingStatuses && <StatusesDialog flow="parts" onClose={() => setEditingStatuses(false)} />}
    </>
  );
}
